import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { getFirebaseDb as getDb } from "./firebase-ultra-safe";
import { Notification, CreateNotificationData } from "../../data/types";

// 알림 생성
export async function createNotification(
  data: CreateNotificationData
): Promise<{ success: boolean; notificationId?: string; error?: string }> {
  const db = await getDb();
  try {
    console.log("알림 생성 시작:", data);

    const notificationsRef = collection(db, "notifications");
    const notificationData = {
      userId: data.userId,
      type: data.type,
      title: data.title,
      message: data.message,
      data: data.data || {},
      isRead: false,
      priority: data.priority || "normal",
      link: data.link,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(notificationsRef, notificationData);
    console.log("알림 생성 완료:", docRef.id);

    return { success: true, notificationId: docRef.id };
  } catch (error) {
    console.error("알림 생성 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "알림 생성에 실패했습니다.",
    };
  }
}

// 사용자의 알림 목록 조회
export async function getUserNotifications(
  userId: string,
  limitCount: number = 20,
  lastDoc?: QueryDocumentSnapshot<DocumentData>
): Promise<{
  success: boolean;
  notifications?: Notification[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  error?: string;
}> {
  const db = await getDb();
  try {
    console.log("사용자 알림 목록 조회:", userId);

    const notificationsRef = collection(db, "notifications");
    // 인덱스 문제를 피하기 위해 orderBy를 제거하고 클라이언트에서 정렬
    let q = query(
      notificationsRef,
      where("userId", "==", userId),
      limit(limitCount * 2) // 더 많이 가져와서 클라이언트에서 정렬 후 제한
    );

    if (lastDoc) {
      q = query(
        notificationsRef,
        where("userId", "==", userId),
        startAfter(lastDoc),
        limit(limitCount * 2)
      );
    }

    const snapshot = await getDocs(q);
    let notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Notification[];

    // 클라이언트에서 시간순 정렬
    notifications = notifications.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime; // 최신순
    });

    // 제한된 개수만 반환
    notifications = notifications.slice(0, limitCount);

    console.log("알림 목록 조회 완료:", notifications.length, "개");
    return {
      success: true,
      notifications,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
    };
  } catch (error) {
    console.error("알림 목록 조회 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "알림 목록을 조회하는데 실패했습니다.",
    };
  }
}

// 읽지 않은 알림 개수 조회
export async function getUnreadNotificationCount(
  userId: string
): Promise<{ success: boolean; count?: number; error?: string }> {
  const db = await getDb();
  try {
    console.log("읽지 않은 알림 개수 조회:", userId);

    const notificationsRef = collection(db, "notifications");
    const q = query(
      notificationsRef,
      where("userId", "==", userId),
      where("isRead", "==", false)
    );

    const snapshot = await getDocs(q);
    const count = snapshot.docs.length;

    console.log("읽지 않은 알림 개수:", count);
    return { success: true, count };
  } catch (error) {
    console.error("읽지 않은 알림 개수 조회 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "읽지 않은 알림 개수를 조회하는데 실패했습니다.",
    };
  }
}

// 알림 읽음 처리
export async function markNotificationAsRead(
  notificationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  const db = await getDb();
  try {
    console.log("알림 읽음 처리:", notificationId, userId);

    const notificationRef = doc(db, "notifications", notificationId);
    const notificationSnap = await getDoc(notificationRef);

    if (!notificationSnap.exists()) {
      return { success: false, error: "알림을 찾을 수 없습니다." };
    }

    const notificationData = notificationSnap.data() as Notification;

    // 권한 확인
    if (notificationData.userId !== userId) {
      return { success: false, error: "권한이 없습니다." };
    }

    // 이미 읽음 처리된 경우
    if (notificationData.isRead) {
      return { success: true };
    }

    // 읽음 처리
    await updateDoc(notificationRef, {
      isRead: true,
      readAt: serverTimestamp(),
    });

    console.log("알림 읽음 처리 완료:", notificationId);
    return { success: true };
  } catch (error) {
    console.error("알림 읽음 처리 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "알림 읽음 처리에 실패했습니다.",
    };
  }
}

// 모든 알림 읽음 처리
export async function markAllNotificationsAsRead(
  userId: string
): Promise<{ success: boolean; error?: string }> {
  const db = await getDb();
  try {
    console.log("모든 알림 읽음 처리:", userId);

    const notificationsRef = collection(db, "notifications");
    const q = query(
      notificationsRef,
      where("userId", "==", userId),
      where("isRead", "==", false)
    );

    const snapshot = await getDocs(q);

    if (snapshot.docs.length === 0) {
      return { success: true };
    }

    // 배치 업데이트
    const updatePromises = snapshot.docs.map(doc => {
      return updateDoc(doc.ref, {
        isRead: true,
        readAt: serverTimestamp(),
      });
    });

    await Promise.all(updatePromises);

    console.log("모든 알림 읽음 처리 완료:", snapshot.docs.length, "개");
    return { success: true };
  } catch (error) {
    console.error("모든 알림 읽음 처리 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "모든 알림 읽음 처리에 실패했습니다.",
    };
  }
}

// 알림 삭제
export async function deleteNotification(
  notificationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  const db = await getDb();
  try {
    console.log("알림 삭제:", notificationId, userId);

    const notificationRef = doc(db, "notifications", notificationId);
    const notificationSnap = await getDoc(notificationRef);

    if (!notificationSnap.exists()) {
      return { success: false, error: "알림을 찾을 수 없습니다." };
    }

    const notificationData = notificationSnap.data() as Notification;

    // 권한 확인
    if (notificationData.userId !== userId) {
      return { success: false, error: "권한이 없습니다." };
    }

    await deleteDoc(notificationRef);
    console.log("알림 삭제 완료:", notificationId);

    return { success: true };
  } catch (error) {
    console.error("알림 삭제 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "알림 삭제에 실패했습니다.",
    };
  }
}

// 모든 알림 삭제
export async function deleteAllNotifications(
  userId: string
): Promise<{ success: boolean; error?: string }> {
  const db = await getDb();
  try {
    console.log("모든 알림 삭제:", userId);

    const notificationsRef = collection(db, "notifications");
    const q = query(notificationsRef, where("userId", "==", userId));

    const snapshot = await getDocs(q);

    if (snapshot.docs.length === 0) {
      return { success: true };
    }

    // 배치 삭제
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    console.log("모든 알림 삭제 완료:", snapshot.docs.length, "개");
    return { success: true };
  } catch (error) {
    console.error("모든 알림 삭제 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "모든 알림 삭제에 실패했습니다.",
    };
  }
}

// 실시간 알림 구독
export function subscribeToNotifications(
  userId: string,
  callback: (notifications: Notification[]) => void,
  onError?: (error: Error) => void
): () => void {
  console.log("실시간 알림 구독 시작:", userId);

  let unsubscribe: (() => void) | null = null;

  // 동기적으로 db를 가져와서 구독 설정
  try {
    const db = getDb();
    const notificationsRef = collection(db, "notifications");
    // 인덱스 문제를 피하기 위해 orderBy를 제거하고 클라이언트에서 정렬
    const q = query(
      notificationsRef,
      where("userId", "==", userId),
      limit(100) // 더 많이 가져와서 클라이언트에서 정렬
    );

    unsubscribe = onSnapshot(
      q,
      snapshot => {
        let notifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Notification[];

        // 클라이언트에서 시간순 정렬
        notifications = notifications.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime; // 최신순
        });

        // 최근 50개만 반환
        notifications = notifications.slice(0, 50);

        console.log("실시간 알림 업데이트:", notifications.length, "개");
        callback(notifications);
      },
      error => {
        console.error("실시간 알림 구독 오류:", error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error("❌ DB 초기화 오류:", error);
    onError?.(error);
  }

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

// 실시간 읽지 않은 알림 개수 구독
export function subscribeToUnreadNotificationCount(
  userId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void
): () => void {
  console.log("실시간 읽지 않은 알림 개수 구독 시작:", userId);

  let unsubscribe: (() => void) | null = null;

  // 동기적으로 db를 가져와서 구독 설정
  try {
    const db = getDb();
    const notificationsRef = collection(db, "notifications");
    const q = query(
      notificationsRef,
      where("userId", "==", userId),
      where("isRead", "==", false)
    );

    unsubscribe = onSnapshot(
      q,
      snapshot => {
        const count = snapshot.docs.length;
        console.log("실시간 읽지 않은 알림 개수:", count);
        callback(count);
      },
      error => {
        console.error("실시간 읽지 않은 알림 개수 구독 오류:", error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error("❌ DB 초기화 오류:", error);
    onError?.(error);
  }

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

// 알림 생성 헬퍼 함수들 (타입별)
export async function createNewMessageNotification(data: {
  userId: string;
  senderName: string;
  productTitle: string;
  productPrice?: number;
  productImage?: string;
  messagePreview: string;
  chatId: string;
}): Promise<{ success: boolean; notificationId?: string; error?: string }> {
  // 시스템 메시지인 경우 (senderName이 빈 문자열) 메시지 내용만 표시
  const isSystemMessage = data.senderName === "";
  const notificationMessage = isSystemMessage
    ? data.messagePreview
    : `${data.senderName}님이 "${data.productTitle}" 상품에 대해 메시지를 보냈습니다: ${data.messagePreview}`;

  return createNotification({
    userId: data.userId,
    type: "new_message",
    title: "새로운 채팅 메시지",
    message: notificationMessage,
    data: {
      senderName: data.senderName,
      productTitle: data.productTitle,
      productPrice: data.productPrice || 0,
      productImage: data.productImage,
      messagePreview: data.messagePreview,
      chatId: data.chatId,
    },
    link: `/chat`,
    priority: "normal",
  });
}

// 화상 통화 시작 알림
export async function createVideoCallNotification(data: {
  userId: string;
  instructorName: string;
  sessionId: string;
  mode?: "call" | "classroom";
}): Promise<{ success: boolean; notificationId?: string; error?: string }> {
  const isClassroom = data.mode === "classroom";
  return createNotification({
    userId: data.userId,
    type: "video_call",
    title: isClassroom ? "강의실 초대" : "화상 통화 요청",
    message: isClassroom
      ? `${data.instructorName} 강사님이 강의실에 초대했습니다. 눌러서 입장해 주세요.`
      : `${data.instructorName} 강사님이 화상 통화를 시작했습니다.`,
    data: { sessionId: data.sessionId, mode: data.mode || "call" },
    link: isClassroom
      ? `/lessons/classroom/${data.sessionId}`
      : `/lessons/call/${data.sessionId}`,
    priority: "urgent",
  });
}

/** 동료 강사 강의실 초대 알림 */
export async function createGuestInstructorInviteNotification(data: {
  userId: string;
  hostInstructorName: string;
  sessionId: string;
}): Promise<{ success: boolean; notificationId?: string; error?: string }> {
  return createNotification({
    userId: data.userId,
    type: "video_call",
    title: "강의실 초대 (동료 강사)",
    message: `${data.hostInstructorName} 강사님이 동료 강사로 초대했습니다.`,
    data: { sessionId: data.sessionId, mode: "classroom", guestInvite: true },
    link: `/lessons/classroom/${data.sessionId}`,
    priority: "urgent",
  });
}

// 거래 상태 업데이트 알림
export async function createTransactionUpdateNotification(data: {
  userId: string;
  transactionId: string;
  status: string;
  productTitle: string;
  amount: number;
  counterpartName: string;
  itemId?: string;
}): Promise<{ success: boolean; notificationId?: string; error?: string }> {
  const statusText = data.status || "상태 업데이트";
  return createNotification({
    userId: data.userId,
    type: "transaction_update",
    title: "거래 상태가 변경되었습니다",
    message: `${data.counterpartName || "상대방"}님과의 거래가 "${statusText}" 상태로 변경되었습니다.`,
    data: {
      transactionId: data.transactionId,
      status: data.status,
      productTitle: data.productTitle,
      amount: data.amount,
      counterpartName: data.counterpartName,
      itemId: data.itemId,
    },
    link: `/profile/transactions`,
    priority: "high",
  });
}

// 피드백 리포트 전달 알림
export async function createFeedbackReportNotification(data: {
  userId: string;
  reportId: string;
  reportTitle: string;
  proName: string;
  hasPdf?: boolean;
  hasVideo?: boolean;
}): Promise<{ success: boolean; notificationId?: string; error?: string }> {
  const contentTypes = [];
  if (data.hasPdf) contentTypes.push("PDF 리포트");
  if (data.hasVideo) contentTypes.push("실시간 피드백");
  const contentTypeText = contentTypes.length > 0 ? contentTypes.join(" 및 ") : "피드백";

  return createNotification({
    userId: data.userId,
    type: "feedback_report",
    title: "피드백 리포트 전달",
    message: `${data.proName} 전문가가 "${data.reportTitle}"에 대한 ${contentTypeText}를 전달했습니다.`,
    data: {
      reportId: data.reportId,
      reportTitle: data.reportTitle,
      proName: data.proName,
      hasPdf: data.hasPdf || false,
      hasVideo: data.hasVideo || false,
    },
    link: `/profile/reports`,
    priority: "high",
  });
}

